import { defineNuxtPlugin } from '#app'
import { client } from '~/src/client/client.gen'

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const publicCfg = config.public as Record<string, string>
  const apiBase = publicCfg.apiBaseUrl || 'https://localhost:44397'
  const oidcBaseUrl = publicCfg.oidcBaseUrl
  const oidcRealm = publicCfg.oidcRealm
  const clientId = publicCfg.oidcClientId
  const redirectUri = publicCfg.oidcRedirectUri

  // Base client config

  // Set base URL
  client.setConfig({ baseUrl: apiBase })

  // Set auth and robust refresh on client
  if (import.meta.client) {
    // Always read latest token when sending requests
    client.setConfig({ auth: () => localStorage.getItem('auth_token') || undefined })

    // Helpers to parse exp and dedupe refresh
    const getJwtExpMs = (jwt: string | null): number | null => {
      if (!jwt) return null
      try {
        const part = jwt.split('.')[1] || ''
        const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
        const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
        const payload = JSON.parse(atob(padded)) as { exp?: number }
        return typeof payload.exp === 'number' ? payload.exp * 1000 : null
      } catch {
        return null
      }
    }

    let refreshInFlight: Promise<void> | null = null
    const ensureFreshToken = async (force = false) => {
      const access = localStorage.getItem('auth_token')
      const refresh = localStorage.getItem('refresh_token')
      if (!refresh) return
      const expMs = getJwtExpMs(access)
      const needsRefresh = force || !expMs || expMs <= Date.now() + 5_000
      if (!needsRefresh) return
      if (!refreshInFlight) {
        const { refreshToken } = useAuth()
        refreshInFlight = (async () => {
          try {
            await refreshToken()
          } finally {
            refreshInFlight = null
          }
        })()
      }
      await refreshInFlight
    }

    // Install a fetch wrapper to refresh on reopen or on 401, and retry once
    client.setConfig({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fetch: async (input: any, init?: any) => {
        try {
          // Pre-flight refresh if needed (covers app reopen with expired token)
          await ensureFreshToken()
        } catch {
          // ignore pre-flight errors; request may still work
        }

        let response = await fetch(input as RequestInfo, init as RequestInit)
        if (response.status !== 401) return response

        // Attempt one refresh and retry once
        try {
          await ensureFreshToken(true)
        } catch {
          // fall through
        }
        response = await fetch(input as RequestInfo, init as RequestInit)
        if (response.status !== 401) return response

        // Still unauthorized: log out and redirect
        try {
          const { logout } = useAuth()
          // Do not await to avoid blocking response handling
          void logout()
        } catch {
          // ignore
        }
        return response
      }
    })

    // Schedule background refresh ~1 minute before expiry, and immediate if already expired
    const scheduleRefresh = () => {
      const access = localStorage.getItem('auth_token')
      const refresh = localStorage.getItem('refresh_token')
      if (!refresh) return
      const expMs = getJwtExpMs(access)
      if (!expMs) return
      const msUntilExpiry = expMs - Date.now()
      const refreshIn = Math.max(msUntilExpiry - 60_000, 0)
      const doRefresh = async () => {
        try {
          await ensureFreshToken(true)
        } catch (err) {
          console.error('Token refresh failed', err)
        } finally {
          scheduleRefresh()
        }
      }
      if (refreshIn === 0) {
        void doRefresh()
      } else {
        setTimeout(doRefresh, Math.max(refreshIn, 5_000))
      }
    }
    scheduleRefresh()
  }

  return {
    provide: {
      apiBase,
      oidc: { oidcBaseUrl, oidcRealm, clientId, redirectUri }
    }
  }
})